import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, Phone, Instagram, Truck, Wallet, ShieldCheck, Headphones, ExternalLink } from "lucide-react";
import { useSettings } from "../../auth/SettingsContext";
import useBrandAsset from "../../lib/useBrandAsset";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Backwards-compat — components that import CONTACT directly still work.
// Prefer `useSettings()` for live values.
export const CONTACT = {
    email: "admin.jns.creation@gmail.com",
    phoneDisplay: "+91 73597 92437",
    phoneTel: "+917359792437",
    phoneWhatsApp: "917359792437",
    instagramHandle: "jns.cration.ds",
    instagramUrl: "https://www.instagram.com/jns.cration.ds",
};

export default function Footer() {
    const s = useSettings();
    const brandLogo = useBrandAsset("brand_logo");
    const year = new Date().getFullYear();
    // iter234 — Gate Custom Studio + Lookbook footer links on the customer-
    // facing entry flag (matches header CTA behaviour). Falls back to master
    // `enabled` for backwards compatibility.
    const [csVisible, setCsVisible] = useState(false);
    // iter234 — Hide "Cash on delivery" trust tile + footer copy when admin
    // has removed COD from `payment_methods` in /config/settings.
    const [codEnabled, setCodEnabled] = useState(true);
    useEffect(() => {
        axios.get(`${API}/custom-studio/state`)
            .then(r => setCsVisible(!!(r.data?.show_homepage_entry ?? r.data?.enabled)))
            .catch(() => setCsVisible(false));
        axios.get(`${API}/settings/public`)
            .then(r => {
                const methods = (r.data?.payment_methods || []).map(m => String(m).toUpperCase());
                setCodEnabled(methods.includes("COD"));
            })
            .catch(() => setCodEnabled(true));
    }, []);
    const phoneTel = (s.contact_phone || "").replace(/\D/g, "");
    const instagramHandle = s.instagram_handle || CONTACT.instagramHandle;
    const instagramUrl = s.instagram_url || (instagramHandle ? `https://www.instagram.com/${instagramHandle}` : CONTACT.instagramUrl);
    return (
        <footer className="bg-ink text-ivory mt-auto print:hidden border-t border-ink-soft/30" data-testid="shop-footer" data-nosnippet>
            {/* Trust tiles row */}
            <div className="border-b border-ink-soft/20 bg-ink-soft/10">
                <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="footer-trust-tiles">
                    <TrustTile icon={Truck} title="Pan-India shipping" sub="Delivered to your doorstep" />
                    {codEnabled && <TrustTile icon={Wallet} title="Cash on delivery" sub="Pay when it arrives" />}
                    <TrustTile icon={ShieldCheck} title="Boutique curated" sub="Hand-picked every piece" />
                    <TrustTile icon={Headphones} title="Real-person support" sub="WhatsApp us anytime" />
                </div>
            </div>

            {/* Instagram block moved to homepage; footer keeps only the
                @handle line in the Contact column below. */}

            <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                    <div>
                        <div className="flex items-center gap-3 mb-3">
                            <img src={brandLogo || "/jns-logo.png"} alt={s.brand_name} className="h-20 w-auto object-contain" />
                            <div>
                                <p className="font-display text-xl text-white leading-none">{s.brand_name}</p>
                                <p className="text-[10px] text-stone-400 mt-1 tracking-wider uppercase">{s.brand_tagline}</p>
                            </div>
                        </div>
                        <p className="text-xs text-stone-400 leading-relaxed">
                            Elegant Indian wear, handpicked and shipped across India.{codEnabled ? " Cash on delivery available." : ""}
                        </p>
                    </div>

                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 mb-3">Shop</p>
                        <ul className="space-y-1.5 text-sm">
                            <li><Link to="/" className="hover:text-ivory-soft" data-testid="footer-link-home">Home</Link></li>
                            {csVisible && <li><Link to="/custom-studio" className="hover:text-ivory-soft" data-testid="footer-link-custom-studio">Custom Studio</Link></li>}
                            {/* Jul 2026 — Lookbook link removed while the page is empty
                                ("being curated"): a promoted destination with no content
                                reads as "site under construction". Restore this line once
                                designs are featured from the admin Custom dashboard. */}
                            <li><Link to="/sale" className="hover:text-ivory-soft" data-testid="footer-link-sale">On Sale</Link></li>
                            <li><Link to="/wishlist" className="hover:text-ivory-soft" data-testid="footer-link-wishlist">My Wishlist</Link></li>
                            <li><Link to="/orders" className="hover:text-ivory-soft" data-testid="footer-link-orders">My Orders</Link></li>
                            <li><Link to="/profile" className="hover:text-ivory-soft" data-testid="footer-link-profile">My Profile</Link></li>
                        </ul>
                    </div>

                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 mb-3">Contact Us</p>
                        <ul className="space-y-2 text-sm">
                            {s.contact_email && (
                                <li>
                                    <a href={`mailto:${s.contact_email}`} data-testid="footer-email" className="inline-flex items-center gap-2 hover:text-ivory-soft">
                                        <Mail size={14} className="text-rosegold" /> {s.contact_email}
                                    </a>
                                </li>
                            )}
                            {phoneTel && (
                                <li>
                                    <a href={`tel:+${phoneTel}`} data-testid="footer-phone" className="inline-flex items-center gap-2 hover:text-ivory-soft">
                                        <Phone size={14} className="text-rosegold" /> {s.contact_phone}
                                    </a>
                                </li>
                            )}
                            {s.whatsapp_number && (
                                <li>
                                    <a href={`https://wa.me/${s.whatsapp_number}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="inline-flex items-center gap-2 hover:text-ivory-soft">
                                        <svg className="w-3.5 h-3.5 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.523 5.281l-.999 3.648 3.965-.628zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/></svg>
                                        WhatsApp us
                                    </a>
                                </li>
                            )}
                            {s.instagram_handle && (
                                <li className="pt-1">
                                    <a href={`https://www.instagram.com/${s.instagram_handle}`} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="inline-flex items-center gap-2 hover:text-ivory-soft text-xs text-stone-400">
                                        <Instagram size={13} /> @{s.instagram_handle}
                                    </a>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>

                <div className="mt-8 pt-5 border-t border-stone-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <p className="text-[11px] text-stone-500">© {year} {s.brand_name}. All rights reserved.</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-stone-500">
                        <Link to="/privacy-policy" className="hover:text-ivory-soft" data-testid="footer-link-privacy">Privacy</Link>
                        <span className="opacity-30">·</span>
                        <Link to="/return-policy" className="hover:text-ivory-soft" data-testid="footer-link-return">Return policy</Link>
                        <span className="opacity-30">·</span>
                        <Link to="/terms" className="hover:text-ivory-soft" data-testid="footer-link-terms">Terms</Link>
                    </div>
                    <p className="text-[11px] text-stone-500">
                        {codEnabled ? "Cash on delivery · " : ""}Pan-India shipping{s.free_delivery_threshold > 0 ? ` · Free over ₹${Number(s.free_delivery_threshold).toLocaleString("en-IN")}` : ""}
                    </p>
                </div>
            </div>
        </footer>
    );
}

function TrustTile({ icon: Icon, title, sub }) {
    return (
        <div className="flex items-center gap-3 p-2.5 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-rosegold/15 border border-rosegold/30 flex items-center justify-center flex-shrink-0">
                <Icon size={18} className="text-rosegold" />
            </div>
            <div className="min-w-0">
                <p className="text-[13px] font-semibold text-ivory leading-tight truncate">{title}</p>
                <p className="text-[10px] text-stone-400 mt-0.5 leading-tight">{sub}</p>
            </div>
        </div>
    );
}

function InstagramBlock() {
    // Instagram moved to homepage `InstagramShowcase`. Keep the function
    // exported as a no-op so any legacy imports don't crash.
    return null;
}
